import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { FirebaseMessagingService } from './firebase-messaging.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly API_URL = environment.host + '/api/notifications';

  constructor(
    private http: HttpClient,
    private firebaseMessaging: FirebaseMessagingService
  ) {
    // Service is now initialized and ready to display notifications
    // when messages are received from Firebase
  }

  /**
   * Enable notifications for a user
   */
  async enableForUser(userId: string): Promise<{ success: boolean; token?: string; error?: string }> {

    try {
      console.log('Notification Service: Enabling for user:', userId);
      // 1. Initialize Firebase
      const initialized = await this.firebaseMessaging.initialize();

      if (!initialized) {
        return { success: false, error: 'Firebase Messaging not supported' };
      }

      // 2. Get FCM token
      const token = await this.firebaseMessaging.requestPermissionAndGetToken();

      if (!token) {
        const error = 'Failed to get FCM token. Please check browser permissions.';
        return { success: false, error };
      }

      console.log('Notification Service: Got token, registering with backend...');

      // 3. Register token with backend
      const registerResult = await this.registerTokenWithBackend(userId, token).toPromise();

      if (registerResult?.success) {
        console.log('Notification Service: Successfully enabled for user');
        console.log('Notification Service: Notifications will now display automatically when sent from backend');
        return { success: true, token };
      } else {
        const error = registerResult?.error || 'Failed to register token with backend';
        return { success: false, error };
      }

    } catch (error) {
      const errorMsg = 'Error enabling notifications: ' + (error as Error).message;
      console.error('Notification Service:', errorMsg);
      return { success: false, error: errorMsg };
    }
  }

  /**
   * Register token with backend API
   */
  private registerTokenWithBackend(userId: string, token: string): Observable<any> {
    return this.http.post(`${this.API_URL}/register-token`, {
      userId: userId,
      fcmToken: token
    }).pipe(
      tap(response => {
        console.log('Notification Service: Token registered with backend', response);
      }),
      catchError(error => {
        console.error('Notification Service: Backend registration failed', error);
        return of({ success: false, error: error.message });
      })
    );
  }

  /**
   * Disable notifications for a user
   */
  async disableForUser(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('Notification Service: Disabling for user:', userId);

      // 1. Delete token from Firebase
      const deleted = await this.firebaseMessaging.deleteToken();

      if (!deleted) {
        console.warn('Notification Service: No token to delete or deletion failed');
      } else {
        console.log('Notification Service: Token deleted from Firebase');
      }

      // 2. Unregister token from backend
      const unregisterResult = await this.http.post(`${this.API_URL}/unregister-token`, {
        userId: userId
      }).toPromise();

      console.log('Notification Service: Successfully disabled for user');
      return { success: true };

    } catch (error) {
      const errorMsg = 'Error disabling notifications: ' + (error as Error).message;
      console.error('Notification Service:', errorMsg);
      return { success: false, error: errorMsg };
    }
  }
}